import React, { useMemo } from 'react';

import { installJankMonitoring } from 'app/perf-utils/JankMonitoring';
import { installPerformanceMonitor } from 'app/perf-utils/PerformanceMonitoring';

const devtoolsMissingMessage = `Unable to load per-developer devtools from 'app/devtools.js'. See 'app/devtools.example.js' for more information.`;

const logCurrentGitBranch = () =>
  console.info(
    `%c</>%c Currently running branch %c${process.env.BRANCH}`,
    'font-family: monospace; font-weight: bold; color: #fff; background-color: #FF4A00; margin: 10px auto; padding: 7px 3px; border-radius: 2px;',
    'font-weight: normal',
    'font-family: monospace; font-weight: bold'
  );

const errorStyle = 'color: #D32F2F';

const getTruncatedStack = pops =>
  new Error().stack
    .split('\n')
    .slice(pops + 2, pops + 5)
    .map(line => '    ' + line.trim())
    .join('\n');

const logExtraneousUseStateCallbackArgs = creationStack => {
  console.info(
    `🚫 %cState updates from the useState() Hook don't support the second callback argument.`,
    errorStyle
  );
  console.info(`%c${creationStack}`, errorStyle);
};

const installImprovedUseStateCallbackWarnings = () => {
  // Let the developer know we're about to do something crazy ...
  console.warn('Shimming React.useState to improve callback arg warnings');

  // Reference the original useState
  const { useState } = React;

  // Monkey patch it to log more informative error
  // messages when the callback is passed multiple args
  React.useState = initialState => {
    const [wasSeen, markAsSeen] = useState(false);
    const [value, setValue] = useState(initialState);

    // Memoize the creation stack of the useState instance
    const creationStack = useMemo(() => getTruncatedStack(5), []);

    const setValueDebug = (newValue, ...args) => {
      if (args.length > 0 && !wasSeen) {
        logExtraneousUseStateCallbackArgs(creationStack);
        markAsSeen(true);
      }
      return setValue(newValue);
    };

    return [value, setValueDebug];
  };
};

const preCreateApp = () => {
  if (process.env.NODE_ENV !== 'production') {
    logCurrentGitBranch();

    installJankMonitoring();
    installPerformanceMonitor(React);

    installImprovedUseStateCallbackWarnings();
  }
};

const postCreateApp = app => {
  if (process.env.NODE_ENV !== 'production') {
    if (process.env.Z_DEVTOOLS) {
      try {
        const devtools = require('app/devtools').default;
        devtools(app.store);
      } catch (e) {
        console.warn(devtoolsMissingMessage);
      }
    } else {
      console.warn(devtoolsMissingMessage);
    }
  }
};

export default {
  preCreateApp,
  postCreateApp,
};
